import type { InventoryPushChannel } from "@redotech/admin-sdk/rpc/schema/ims/get-inventory-push-settings";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { memo } from "react";

export const inventoryPushForm = groupInput({ shopify: input<boolean>() });

export type InventoryPushForm = InputSchema.Input<typeof inventoryPushForm>;

export type InventoryPushValue = InputSchema.Value<typeof inventoryPushForm>;

export const inventoryPushDefault: InventoryPushValue = { shopify: false };

const CHANNEL_LABELS: Record<InventoryPushChannel, string> = {
  shopify: "Shopify",
};

export const inventoryPushChannels: InventoryPushChannel[] = ["shopify"];

export function anyInventoryPushEnabled(value: InventoryPushValue): boolean {
  return inventoryPushChannels.some((channel) => value[channel] === true);
}

/** One inventory push toggle per sales channel. */
export const InventoryPushToggles = memo(function InventoryPushToggles({
  input,
  connected,
  blockTurningOn,
}: {
  input: InventoryPushForm;
  /** Channels the team has an enabled integration for. */
  connected: Record<InventoryPushChannel, boolean>;
  /** Why push can't be turned on right now; turning it off is always allowed. */
  blockTurningOn?: string;
}) {
  return (
    <Flex dir="column" gap="md">
      {inventoryPushChannels.map((channel) => {
        const on = input.inputs[channel].value ?? false;
        const blocked = !!blockTurningOn && !on;
        return (
          <RedoToggle
            description={
              !connected[channel]
                ? `No enabled ${CHANNEL_LABELS[channel]} integration`
                : blocked
                  ? blockTurningOn
                  : `Push Redo inventory levels to ${CHANNEL_LABELS[channel]}`
            }
            disabled={!connected[channel] || blocked}
            key={channel}
            label={`${CHANNEL_LABELS[channel]} Inventory Push`}
            setValue={(value) => input.inputs[channel].setValue(value)}
            size="sm"
            value={on}
          />
        );
      })}
    </Flex>
  );
});
